<template>
  <section class="section is-main-section">
    <card-component
      has-back
      title="Create New Driver"
      icon="video-input-component"
      class="tile is-child"
    >
      <template #headerRight>
        <file-picker
          v-model="importForm.file"
          class="mr-4"
          icon-left="file"
          size="is-small"
          label="Import"
          accept=".apl"
          :loading="importForm.isLoading"
          @input="importDriver"
        />
      </template>
      <form>
        <b-field label="Name">
          <b-input placeholder="Driver name" v-model="form.name" />
        </b-field>
        <hr />
        <b-tabs
          v-model="activeTab"
          class="is-flipped"
          size="is-small"
          type="is-boxed"
        >
          <b-tab-item
            v-for="tab in formSetting"
            :key="tab.label"
            :label="tab.label"
          >
            <div v-for="(item, index) in tab.contents" :key="index">
              <div style="margin-bottom: 0.5rem">
                <b>{{ item.label }}</b>
              </div>
              <b-field horizontal class="no-label">
                <editable-select
                  v-model="form.regexes[item.key].wordList"
                  expanded
                />
                <b-input
                  v-model="form.regexes[item.key].start"
                  placeholder="Start"
                  type="number"
                />
                <b-input
                  v-model="form.regexes[item.key].length"
                  placeholder="Length"
                  type="number"
                />
                <b-input
                  v-model="form.regexes[item.key].field"
                  placeholder="Field"
                  type="number"
                />
              </b-field>
              <b-field>
                <b-input
                  v-model="form.regexes[item.key].format"
                  placeholder="Format"
                />
              </b-field>
              <hr v-if="index !== tab.contents.length - 1" />
            </div>
            <template v-if="tab.label === 'Message Bounds'">
              <hr style="margin-top: 1em" />
              <b-field label="End of line">
                <b-input placeholder="End of line" v-model="form.end_of_line" />
              </b-field>
            </template>
          </b-tab-item>
          <b-tab-item label="Conversions">
            <b-field grouped class="mb-2">
              <b-field class="f-1" label="Element" expanded />
              <b-field class="f-1" label="Replace" expanded />
              <b-field class="f-1" label="With" expanded />
              <span class="has-text-white">aaaa</span>
            </b-field>
            <template v-for="(item, index) in form.conversions">
              <b-field :key="`conv-${index}`" grouped>
                <b-select
                  placeholder="Select Element"
                  v-model="form.conversions[index].element"
                  expanded
                >
                  <option
                    v-for="el in conversionElements"
                    :value="el.value"
                    :key="el.value"
                  >
                    {{ el.label }}
                  </option>
                </b-select>
                <b-input
                  placeholder="Replace"
                  v-model="form.conversions[index].replace"
                  expanded
                />
                <b-input
                  placeholder="With"
                  v-model="form.conversions[index].with"
                  expanded
                />
                <p class="control">
                  <b-button @click="delConversion(index)">
                    <b-icon icon="delete" custom-size="default"></b-icon>
                  </b-button>
                </p>
              </b-field>
            </template>
            <b-field>
              <b-button @click="addConversion">Add Conversion</b-button>
            </b-field>
            <hr />
          </b-tab-item>
        </b-tabs>
        <b-field label="Message Format">
          <b-radio
            v-model="form.message_format"
            class="mr-6"
            name="messageFormat"
            native-value="ascii"
          >
            ASCII
          </b-radio>
          <b-radio
            v-model="form.message_format"
            name="messageFormat"
            native-value="hex"
          >
            Hexadecimal
          </b-radio>
        </b-field>
        <hr />
        <b-field>
          <b-button
            type="is-primary"
            :loading="isLoading"
            @click.prevent="submit"
            >Submit</b-button
          >
        </b-field>
      </form>
    </card-component>
  </section>
</template>

<script>
import { create as createDriver, importDriver } from '@/api/driver'

import EditableSelect from '@/components/form/EditableSelect'
import FilePicker from '@/components/form/FilePicker'

export default {
  name: 'CreateDriver',
  components: {
    EditableSelect,
    FilePicker
  },
  data() {
    const defaultRegex = {
      start: null,
      length: null,
      field: null,
      wordList: [],
      format: null
    }
    return {
      isLoading: false,
      deviceTypes: [],
      activeTab: 0,
      conversionElements: [
        {
          label: 'Panel ID',
          value: 'panelID'
        },
        {
          label: 'Panel Status',
          value: 'panelStatus'
        },
        {
          label: 'Device Type',
          value: 'deviceType'
        },
        {
          label: 'Device Condition',
          value: 'deviceCondition'
        },
        {
          label: 'Device Address',
          value: 'deviceAddress'
        },
        {
          label: 'Description 1',
          value: 'description1'
        },
        {
          label: 'Description 2',
          value: 'description2'
        }
      ],
      formSetting: [
        {
          label: 'Message Bounds',
          contents: [
            { key: 'strip', label: 'Strip at start' },
            { key: 'start', label: 'Start of event' },
            { key: 'end', label: 'End of event' }
          ]
        },
        {
          label: 'Event Type',
          contents: [
            { key: 'alarm', label: 'Alarm' },
            { key: 'trouble', label: 'Trouble' },
            { key: 'normal', label: 'Normal' }
          ]
        },
        {
          label: 'Date & Time',
          contents: [
            { key: 'date', label: 'Event Date' },
            { key: 'time', label: 'Event Time' }
          ]
        },
        {
          label: 'Panel',
          contents: [
            { key: 'panelID', label: 'Panel ID' },
            { key: 'reset', label: 'Reset' },
            { key: 'panelStatus', label: 'Panel Status' }
          ]
        },
        {
          label: 'Device',
          contents: [
            { key: 'deviceAddress', label: 'Address' },
            { key: 'deviceType', label: 'Type' },
            { key: 'deviceCondition', label: 'Condition' }
          ]
        },
        {
          label: 'Descriptions',
          contents: [
            { key: 'description1', label: 'Description 1' },
            { key: 'description2', label: 'Description 2' }
          ]
        },
        {
          label: 'System Event',
          contents: [
            { key: 'heartbeat', label: 'Heartbeat' },
            { key: 'ignore', label: 'Ignore' }
          ]
        }
      ],
      form: {
        name: null,
        regexes: {
          strip: Object.assign({}, defaultRegex),
          start: Object.assign({}, defaultRegex),
          end: Object.assign({}, defaultRegex),
          alarm: Object.assign({}, defaultRegex),
          trouble: Object.assign({}, defaultRegex),
          normal: Object.assign({}, defaultRegex),
          date: Object.assign({}, defaultRegex),
          time: Object.assign({}, defaultRegex),
          panelID: Object.assign({}, defaultRegex),
          reset: Object.assign({}, defaultRegex),
          panelStatus: Object.assign({}, defaultRegex),
          deviceAddress: Object.assign({}, defaultRegex),
          deviceType: Object.assign({}, defaultRegex),
          deviceCondition: Object.assign({}, defaultRegex),
          description1: Object.assign({}, defaultRegex),
          description2: Object.assign({}, defaultRegex),
          heartbeat: Object.assign({}, defaultRegex),
          ignore: Object.assign({}, defaultRegex)
        },
        end_of_line: null,
        conversions: [{
          element: null,
          replace: null,
          with: null
        }]
      },
      importForm: {
        file: null,
        isLoading: false
      },
    }
  },
  methods: {
    addConversion() {
      if (!this.form.conversions) {
        this.$set(this.form, 'conversions', [{
          element: null,
          replace: null,
          with: null
        }])
      } else {
        this.form.conversions.push({
          element: null,
          replace: null,
          with: null
        })
      }
    },
    delConversion(idx) {
      this.form.conversions.splice(idx, 1)
    },
    submit() {
      this.isLoading = true

      const body = {
        ...this.form,
        regexes: this.form.regexes,
        conversions: this.form.conversions
      }

      createDriver(body)
        .then(() => {
          this.$router.back()
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    importDriver(file) {
      this.importForm.isLoading = true
      const formData = new FormData

      formData.append('file', file)

      return importDriver(formData)
        .then(res => {
          this.form = {
            ...res.data,
            regexes: JSON.parse(res.data.regexes),
            conversions: res.data.conversions && JSON.parse(res.data.conversions)
          }
          this.$buefy.toast.open({
            message: 'Import driver success',
            type: 'is-success'
          })
        })
        .catch(e => {
          this.$buefy.toast.open({
            message: e.response.data,
            type: 'is-danger'
          })
        })
        .finally(() => {
          this.importForm.file = null
          this.importForm.isLoading = false
        })
    }
  }
}
</script>
